import {
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
} from "@mui/x-charts";
import { useCfmContext } from "../../../Context/CfmContext";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import { useMemo, useRef } from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";

const CombinedIncomeExpenditureGraph = ({
  isPrint = false,
  animated = false,
  apiRef = null,
  idOverride,
}) => {
  const { state } = useCfmContext();
  const chartOnlyRef = useRef();

  const combinedData = useMemo(() => {
    const keys = [
    "salary",
      "pensionRegularIncome",
      "statePension",
      "dbPension",
      "investmentRegularIncome",
      "btlRental",
      "otherIncome",
      "adhocIncome",
      "pensionAdhocContribution",
      "pensionAdhocWithdrawal",
      "investmentAdhocContribution",
      "investmentAdhocWithdrawal",
      "taxFreeCash",
    ];

    const colorMap = {
      pensionRegularIncome: "#4299E1",
      taxFreeCash: "#48BB78",
      investmentRegularIncome: "#9F7AEA",
      btlRental: "#F6AD55",
      dbPension: "#38B2AC",
      statePension: "#4C51BF",
      salary: "#ED64A6",
      otherIncome: "#667EEA",
      adhocIncome: "#F56565",
      pensionAdhocWithdrawal: "#4FD1C5",
      pensionAdhocContribution: "#00AD3F",
      investmentAdhocContribution: "#B794F4",
      investmentAdhocWithdrawal: "#FC8181",
      totalExpenditure: "#776096",
    };

    const parseMoney = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "0").replace(/[^0-9.-]+/g, "")) || 0;

    // --- find the latest year allowed (earliest DOB + 100) ---
    let maxYearAllowed = Infinity;
    if (Array.isArray(state.details) && state.details.length > 0) {
      const dobYears = state.details
        .map((d) => {
          const dob = d?.dob;
          if (!dob) return null;
          // supports YYYY-MM-DD or DD/MM/YYYY
          const parts = String(dob).split(/[-/]/);
          if (parts.length < 3) return null;
          const year =
            parts[0].length === 4 ? Number(parts[0]) : Number(parts[2]);
          return Number.isFinite(year) ? year : null;
        })
        .filter((y) => y != null);
      if (dobYears.length > 0) {
        const earliestDobYear = Math.min(...dobYears);
        maxYearAllowed = earliestDobYear + 100;
      }
    }

    // 1) longest series length across all people
    const maxLen =
      state.incomeExpenditureResults?.reduce((m, entry) => {
        const len = entry?.incomeExpenditure?.length || 0;
        return Math.max(m, len);
      }, 0) || 0;

    // 2) aggregate by index
    const byIdx = Array.from({ length: maxLen }, () => {
      const base = { totalExpenditure: 0 };
      keys.forEach((k) => (base[k] = 0));
      return base;
    });

    state.incomeExpenditureResults?.forEach((entry) => {
      entry.incomeExpenditure?.forEach((row, idx) => {
        const b = byIdx[idx];
        if (!b) return;
        keys.forEach((k) => (b[k] += parseMoney(row[k])));
        b.totalExpenditure += parseMoney(row.totalExpenditure);
      });
    });

    // 3) build years from startYear, but cap when any person hits 100
    const startYear =
      Number(state?.timelineStartYear) || new Date().getFullYear();

    const maxCountByAgeCap =
      maxYearAllowed === Infinity
        ? maxLen
        : Math.max(0, Math.min(maxLen, maxYearAllowed - startYear + 1));

    const years = Array.from(
      { length: maxCountByAgeCap },
      (_, i) => startYear + i
    );

    // slice to same length
    const indexes = Array.from({ length: years.length }, (_, i) => i);

    // 4) build series
    const series = keys.map((key) => ({
      type: "bar",
      stack: "income",
      data: indexes.map((i) => byIdx[i][key]),
      label:
        key === "dbPension"
          ? "DB Pension"
          : key === "btlRental"
          ? "BTL Rental"
          : key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (s) => s.toUpperCase()),
      color: colorMap[key],
    }));

    // line for combined total expenditure
    series.push({
      type: "line",
      data: indexes.map((i) => byIdx[i].totalExpenditure),
      label: "Combined Total Expenditure",
      color: colorMap.totalExpenditure,
    });

    const visibleSeries = series.filter((s) =>
      s.data.some((v) => Number(v ?? 0) !== 0)
    );

    return { years, visibleSeries };
  }, [state.incomeExpenditureResults, state.details, state?.timelineStartYear]);

  const graphHeight = useGraphHeight(isPrint);

  return (
    <div
      ref={chartOnlyRef}
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "combined-inc-expd-graph"}
    >
      <ChartDataProviderPro
        apiRef={apiRef}
        key="combined-income-expenditure"
        series={combinedData.visibleSeries}
        height={graphHeight}
        fillMode="none"
        xAxis={[
          {
            id: "index-axis",
            data: combinedData.years,
            scaleType: "band",
            label: "Index",
            valueFormatter: (val) => `${val}`,
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Amount",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={!!apiRef}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <BarPlot />
            <LinePlot
              slotProps={{
                line: {
                  style: { strokeWidth: 4 },
                },
              }}
            />
            <ChartsXAxis
              axisId="index-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
    </div>
  );
};

export default CombinedIncomeExpenditureGraph;
